import React, {useState, useContext, useEffect} from 'react';
import {makeStyles} from '@mui/styles';
import Grid from '@mui/material/Grid';
import {NewProjectContext} from '../utils/NewProjectContext';
import ProjectTitle from '../components/ProjectTitle';
import ProjectDescription from '../components/ProjectDescription';
import DynamicInput from '../components/DynamicInput';
import ProjectAddress from '../components/ProjectAddress';
import ImageUploadStep from './ImageUploadStep';
import {CUSTOM_EVENT_NAMES, fireCustomEvent} from '../../../util/Analytics';
import {useHistory} from 'react-router-dom';
import {reloadRfpsState} from '../../../recoil/reloadRfps/reloadRfpsAtom';
import {useSetRecoilState} from 'recoil';
import PublishAndPromoteStep from './PublishAndPromoteStep';
import ProjectPriorities from '../components/ProjectPriorities';
import ProjectInvolvement from '../components/ProjectInvolvement';
import useSnackbar from '../../../hooks/useSnackbar';
import ProjectEstimate from '../components/ProjectEstimate';

const useStyles = makeStyles(() => ({
  root: {
    height: '100vh',
  },
}));

interface IProps {
  templateKey: string | null;
  iframe?: boolean;
  skipTitle?: boolean;
}

const ProjectQuestionsStep = (props: IProps) => {
  const classes = useStyles();
  const history = useHistory();
  const setReloadRfps = useSetRecoilState(reloadRfpsState);
  const {openSnackbar} = useSnackbar();

  const {
    project,
    setProject,
    template,
    setTemplate,
    setNewProjectStep,
    questionIndex,
    setQuestionIndex,
    homeDepot,
  } = useContext(NewProjectContext);

  const [forward, setForward] = useState(true);

  useEffect(() => {
    if (props.skipTitle) {
      setQuestionIndex((prev: number) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notifyProjectCreated = (state: string) => {
    if (project.templateId?.includes('designmii')) {
      openSnackbar(
        'You will be notified when your Designmii documents are ready.',
      );
    } else if (state === 'OPEN') {
      openSnackbar(
        'Congratulations! Your project has been posted and is open for bidding!',
      );
    } else {
      openSnackbar(
        'Project saved as draft. Click PUBLISH to open for bidding.',
      );
    }
  };

  const projectSubmitCallback = (state: string) => {
    fireCustomEvent(CUSTOM_EVENT_NAMES.NEW_PROJECT_WIZARD_COMPLETED);
    if (localStorage.getItem('rfp')) {
      localStorage.removeItem('rfp');
    }
    if (localStorage.getItem('prePackagedProject')) {
      localStorage.removeItem('prePackagedProject');
    }

    if (homeDepot) {
      setNewProjectStep(3);
      return;
    }

    if (!localStorage.getItem('isAnonymous') && !props.iframe) {
      notifyProjectCreated(state);
      setNewProjectStep(0);
      history.push(`/myRfps/${project.uid}?projectCreated=true`);
      setProject({});
      setReloadRfps(true);
    } else {
      setNewProjectStep(2);
    }
  };

  const changeStepHandler = async (direction: number) => {
    await setForward(direction === 1);
    setQuestionIndex((prev: number) => prev + direction);
  };

  const flagBadPrompt = () => {
    setTemplate(null);
    setTimeout(function () {
      setQuestionIndex(1);
    }, 500);
  };

  const dynamicInputs = template?.prompts
    ? template.prompts.map((prompt: $TSFixMe) => (
        <DynamicInput
          key={prompt.uid}
          templateQuestion={prompt}
          changeStepHandler={changeStepHandler}
          flagBadPrompt={flagBadPrompt}
          forward={forward}
        />
      ))
    : [];

  const inputs = [
    <ProjectTitle
      changeStepHandler={changeStepHandler}
      forward={forward}
      defaultValue={project?.title || template?.name}
    />,
    <ProjectPriorities
      changeStepHandler={changeStepHandler}
      forward={forward}
    />,
    <ProjectInvolvement
      changeStepHandler={changeStepHandler}
      forward={forward}
    />,
    ...dynamicInputs,
    <ProjectDescription
      changeStepHandler={changeStepHandler}
      forward={forward}
      defaultValue={project?.description}
    />,
    <ProjectAddress changeStepHandler={changeStepHandler} forward={forward} />,
    <ProjectEstimate changeStepHandler={changeStepHandler} forward={forward} />,
    <ImageUploadStep
      projectSubmitCallback={projectSubmitCallback}
      changeStepHandler={changeStepHandler}
      forward={forward}
    />,
    <PublishAndPromoteStep
      projectSubmitCallback={projectSubmitCallback}
      changeStepHandler={changeStepHandler}
      forward={forward}
    />,
  ];

  const renderQuestionInput = () => {
    return inputs[questionIndex];
  };

  return (
    <div className={classes.root}>
      <Grid container height="100%" justifyContent="center" alignItems="center">
        {renderQuestionInput()}
      </Grid>
    </div>
  );
};

export default ProjectQuestionsStep;
